// Google · Cuenta — qué servicios de Google tiene conectados la persona y su
// organización, en una sola vista. Cada uno se administra en su pestaña.

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, ChevronRight, Megaphone } from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import { getAuthStatus, type OAuthStatus } from '@/lib/api_google_ads';
import { Card, PageHeader } from '@/modules/orion-shell/ui';

import { getCalendarStatus, type EstadoCalendarios } from '../api';
import { conGuiones } from './GoogleAdsView';
import { Insignia } from './shared';

type Fila = { icon: ReactNode; titulo: string; detalle: string; tono: 'ok' | 'aviso' | 'neutro'; estado: string; to: string };

export function CuentaView() {
  const { isEnabled } = useModules();
  const conMarketing = isEnabled('marketing');
  const [cal, setCal] = useState<EstadoCalendarios | null | 'error'>(null);
  const [ads, setAds] = useState<OAuthStatus | null | 'error'>(null);

  useEffect(() => {
    getCalendarStatus().then(setCal).catch(() => setCal('error'));
    if (conMarketing) getAuthStatus().then(setAds).catch(() => setAds('error'));
  }, [conMarketing]);

  const filas: Fila[] = [];
  if (cal !== null) {
    const m = cal === 'error' ? null : cal.mi_conexion;
    filas.push({
      icon: <CalendarDays size={18} />,
      titulo: 'Google Calendar (tuyo)',
      detalle: cal === 'error' ? 'No se pudo consultar.' : m && m.estado !== 'revoked' ? m.google_email : 'El agente aún no puede agendarte citas.',
      tono: m?.estado === 'active' ? 'ok' : m?.estado === 'needs_reconnect' || cal === 'error' ? 'aviso' : 'neutro',
      estado: m?.estado === 'active' ? 'Conectado' : m?.estado === 'needs_reconnect' ? 'Reconectar' : 'Sin conectar',
      to: '/m/google/calendario',
    });
  }
  if (conMarketing && ads !== null) {
    const ok = ads !== 'error' && ads.connected;
    filas.push({
      icon: <Megaphone size={18} />,
      titulo: 'Google Ads (de la organización)',
      detalle: ads === 'error' ? 'No se pudo consultar.' : ok ? `Cuenta ${ads.google_customer_id ? conGuiones(ads.google_customer_id) : 'sin elegir'}` : 'Sin conectar.',
      tono: ok ? 'ok' : ads === 'error' || ads.error_message ? 'aviso' : 'neutro',
      estado: ok ? 'Conectado' : 'Sin conectar',
      to: '/m/google/ads',
    });
  }

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <PageHeader title="Google" subtitle="Tus servicios de Google conectados al sistema. El agente usa solo lo que conectes aquí." />
      <Card>
        {filas.length === 0 ? <p className="text-sm text-muted-foreground">Cargando…</p> : (
          <ul className="divide-y divide-border">
            {filas.map((f) => (
              <li key={f.to}>
                <Link to={f.to} className="-mx-2 flex items-center gap-3 rounded-xl px-2 py-3 hover:bg-muted">
                  <span className="orion-tone tone-violet flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" aria-hidden>{f.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground">{f.titulo}</span>
                    <span className="block truncate text-xs text-muted-foreground">{f.detalle}</span>
                  </span>
                  <Insignia tono={f.tono}>{f.estado}</Insignia>
                  <ChevronRight size={16} className="shrink-0 text-muted-foreground" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <p className="mt-3 text-xs text-muted-foreground">
        Calendar se conecta por persona: cada quien conecta el suyo. Google Ads es uno por organización y lo conecta un administrador.
      </p>
    </div>
  );
}
